
import React, { createContext, useContext, useState, useEffect } from 'react';
import { AnalyticsInsight, UsageData, TrendsData } from '@/types/analytics';
import { AnalyticsService } from '@/services/analyticsService';
import { analyticsTrackingService } from '@/services/analytics/AnalyticsTrackingService';

export type { AnalyticsInsight };

export interface AnalyticsContextType {
  trackEvent: (event: string) => void;
  insights: AnalyticsInsight[];
  markInsightAsRead: (id: string) => void;
  isCollectingData: boolean;
  toggleDataCollection: () => void;
  trackCalculatorInput: (field: string, value: any) => void;
  isAnalyticsEnabled: boolean;
  unreadInsightsCount: number;
  usageData: UsageData;
  trendsData: TrendsData;
  loading: boolean;
  // Risk Assessment Analytics
  trackRiskAssessment: (riskData: import('@/types/riskAssessment').RiskAssessmentData) => void;
  trackReportGeneration: (riskData: import('@/types/riskAssessment').RiskAssessmentData) => void;
  riskAssessmentHistory: import('@/types/riskAssessment').RiskAssessmentData[];
  getRiskTrends: () => Array<{ timestamp: string; riskScore: number; riskLevel: string }>;
}

const defaultContext: AnalyticsContextType = {
  trackEvent: () => {},
  insights: [],
  markInsightAsRead: () => {},
  isCollectingData: false,
  toggleDataCollection: () => {},
  trackCalculatorInput: () => {},
  isAnalyticsEnabled: false,
  unreadInsightsCount: 0,
  usageData: { chartData: [], totalSessions: 0, totalInteractions: 0 },
  trendsData: { pieData: [], priceData: [] },
  loading: true,
  // Risk Assessment defaults
  trackRiskAssessment: () => {},
  trackReportGeneration: () => {},
  riskAssessmentHistory: [],
  getRiskTrends: () => [],
};

const AnalyticsContext = createContext<AnalyticsContextType>(defaultContext);

export const useAnalytics = () => useContext(AnalyticsContext);

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [insights, setInsights] = useState<AnalyticsInsight[]>([]);
  const [usageData, setUsageData] = useState<UsageData>(defaultContext.usageData);
  const [trendsData, setTrendsData] = useState<TrendsData>(defaultContext.trendsData);
  const [loading, setLoading] = useState(true);
  const [isCollectingData, setIsCollectingData] = useState<boolean>(false);
  const [riskAssessmentHistory, setRiskAssessmentHistory] = useState<import('@/types/riskAssessment').RiskAssessmentData[]>([]);
  
  // Calculate unread insights count
  const unreadInsightsCount = insights.filter(insight => !insight.read).length;
  
  // Load settings from localStorage
  useEffect(() => {
    const savedSetting = localStorage.getItem('analyticsEnabled');
    if (savedSetting !== null) {
      setIsCollectingData(savedSetting === 'true');
    } else {
      // Default to true if no setting saved
      setIsCollectingData(true);
      localStorage.setItem('analyticsEnabled', 'true');
    }
  }, []);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!isCollectingData) {
        setLoading(false);
        // Clear data if tracking is disabled
        setInsights([]);
        setUsageData(defaultContext.usageData);
        setTrendsData(defaultContext.trendsData);
        return;
      }
      setLoading(true);
      try {
        const [usage, trends, insightsData] = await Promise.all([
          AnalyticsService.getUsageData(),
          AnalyticsService.getTrendsData(),
          AnalyticsService.getInsights(),
        ]);
        setUsageData(usage);
        setTrendsData(trends);
        setInsights(insightsData);
      } catch (error) {
        console.error("Failed to fetch analytics data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [isCollectingData]);
  
  const trackEvent = (event: string) => {
    if (!isCollectingData) return;
    
    analyticsTrackingService.trackFeatureUsage(event, 'view');
  };

  const trackCalculatorInput = (field: string, value: any) => {
    if (!isCollectingData) return;
    
    analyticsTrackingService.trackCalculatorInput(field, value, 'mortgage');
  };
  
  const markInsightAsRead = (id: string) => {
    setInsights(prev => 
      prev.map(insight => 
        insight.id === id ? { ...insight, read: true } : insight
      )
    );
  };
  
  const toggleDataCollection = () => {
    const newValue = !isCollectingData;
    setIsCollectingData(newValue);
    localStorage.setItem('analyticsEnabled', String(newValue));
  };

  // Risk Assessment Analytics Functions
  const trackRiskAssessment = (riskData: import('@/types/riskAssessment').RiskAssessmentData) => {
    if (!isCollectingData) return;
    
    setRiskAssessmentHistory(prev => [riskData, ...prev.slice(0, 49)]); // Keep last 50 assessments
    
    analyticsTrackingService.trackEvent(
      'feature_used',
      'report',
      'create',
      {
        riskLevel: riskData.riskLevel,
        riskScore: riskData.riskScore,
        timestamp: riskData.timestamp
      }
    );
  };

  const trackReportGeneration = (riskData: import('@/types/riskAssessment').RiskAssessmentData) => {
    if (!isCollectingData) return;
    
    const reportRiskData = { ...riskData, reportGenerated: true, triggerEvent: 'report_generation' as const };
    trackRiskAssessment(reportRiskData);
    
    analyticsTrackingService.trackReportGeneration('risk_assessment', {
      riskLevel: riskData.riskLevel,
      riskScore: riskData.riskScore
    });
  };

  const getRiskTrends = () => {
    return riskAssessmentHistory
      .slice(0, 10)
      .map(assessment => ({
        timestamp: assessment.timestamp,
        riskScore: assessment.riskScore,
        riskLevel: assessment.riskLevel
      }));
  };
  
  const value: AnalyticsContextType = {
    trackEvent,
    insights,
    markInsightAsRead,
    isCollectingData,
    toggleDataCollection,
    trackCalculatorInput,
    isAnalyticsEnabled: isCollectingData,
    unreadInsightsCount,
    usageData,
    trendsData,
    loading,
    // Risk Assessment Analytics
    trackRiskAssessment,
    trackReportGeneration,
    riskAssessmentHistory,
    getRiskTrends,
  };
  
  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
